import { useCallback, useEffect, useState } from "react";
import { cachedGet } from "../api";
import { useAmazonContext } from "../../Contexts/AmazonContext";

const COURSES_TTL = 300000;
const RETRY_DELAY = 1500;

export const courseSlug = (course) => course?.courseTitle?.replace(/\s/g, "");

export const matchesCourse = (course, slug) =>
  Boolean(slug) && courseSlug(course) === slug;

/**
 * Resolve the course record for the course in the URL.
 *
 * courseDetails lives in AmazonContext and is shared across screens, so on mount it can
 * still hold the previous course or have been cleared to null. This hook only reports a
 * course once it matches the requested slug, which keeps callers from rendering against
 * null and from fetching lesson data with the previous course's collection name.
 *
 * Another screen's late response can overwrite the shared record after this hook has
 * resolved, so it resolves again whenever the record stops matching the slug.
 *
 * error is "failed" when /coursesData could not be loaded and "notFound" when no course
 * has this slug.
 */
export function useCourseDetails(slug) {
  const { courseDetails, setCourseDetails } = useAmazonContext();
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const matched = matchesCourse(courseDetails, slug) ? courseDetails : null;
  const isMatched = Boolean(matched);

  useEffect(() => {
    if (!slug || isMatched) return undefined;

    let cancelled = false;
    let retryTimer;
    setError(null);

    const load = (retriesLeft) => {
      cachedGet("/coursesData", { ttl: COURSES_TTL })
        .then((allCourses) => {
          if (cancelled) return;
          const courseChosen = allCourses.find(
            (course) => courseSlug(course) === slug
          );
          if (!courseChosen) setError("notFound");
          setCourseDetails(courseChosen || null);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Error fetching course details:", err);
          if (retriesLeft > 0) {
            retryTimer = setTimeout(() => load(retriesLeft - 1), RETRY_DELAY);
          } else {
            setError("failed");
          }
        });
    };

    load(1);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [slug, isMatched, attempt, setCourseDetails]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    courseDetails: matched,
    isReady: isMatched,
    error: isMatched ? null : error,
    retry,
  };
}
